import React from "react";
import SectionHeader from "@/components/SectionHeader";
import WorkCard from "@/components/WorkCard";

const IPS = [
  {
    index: "01",
    tag: "Monthly · Fund Overhaul",
    title: "Big Changes in Popular Funds.",
    caption: "Tracking monthly portfolio adjustments at India's most-followed mutual funds: Parag Parikh, Nippon Large Cap, Bandhan Small Cap; and decoding what fund managers' moves mean for you.",
    image: "01 Content Pillar - Changes at top funds.jpg",
    link: "https://www.instagram.com/p/DZsCsFEkkIS",
  },
  {
    index: "02",
    tag: "Sector Deep-Dive",
    title: "Stocks Behind the Ecosystem.",
    caption: "Don't talk about stocks; map the entire industry. Railway, pharma, semiconductors… same format, infinite applications.",
    image: "02 Content Pillar - Discussing the ecosystems of different sectors.jpg",
    link: "https://www.instagram.com/p/DVGktrJkhrS/",
  },
  {
    index: "03",
    tag: "Fund Allocation",
    title: "₹100 Invested in [Fund].",
    caption: "Visualising where every rupee goes, stock by stock.",
    image: "03 Content Pillar - Rs 100 invested in funds.jpg",
    link: "https://www.instagram.com/p/DZPw89TSjUN/",
  },
  {
    index: "04",
    tag: "Fund Strategy Shifts",
    title: "9 Stocks Where Mutual Funds Raised Stake.",
    caption: "This helped us cater to both stock and mutual fund investors equally.",
    image: "04 Content Pillar - Where mutual funds raised stake.jpg",
    link: "https://www.instagram.com/p/DYUhNS3kr0C",
  },
];

export default function ContentPillarsPage() {
  return (
    <>
      <SectionHeader
        eyebrow="Case Study 03"
        pageIndex="03"
        title="Strategic Content IPs"
        kicker="Transforming audience obsessions into systematic content products."
        lede="People follow financial accounts for predictable, high-value insights they can't get elsewhere. By identifying what audiences liked on the Instagram page, we built repeatable formats around those pieces. This way, we created content IPs: recurring series that audiences looked forward to."
      />

      {/* Approach */}
      <section className="mx-auto max-w-[1440px] px-6 pt-20 lg:px-12">
        <ol className="grid gap-px overflow-hidden border border-[#EDE4D3]/12 bg-[#EDE4D3]/10 md:grid-cols-2 lg:grid-cols-4">
          {[
            ["Study", "What's getting saved, shared, commented on."],
            ["Find the gap", "What investors want but nobody covers well."],
            ["Build", "A repeatable format. Predictable. Returnable."],
            ["Execute", "Consistency over perfection."],
          ].map(([h, d], idx) => (
            <li key={h} className="bg-[#0B0E14] p-7">
              <p className="font-serif text-xs italic text-[#D4A574]">0{idx + 1}</p>
              <p className="mt-2 font-serif text-2xl">{h}.</p>
              <p className="mt-3 text-sm text-[#EDE4D3]/65">{d}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* IPs */}
      <section className="mx-auto max-w-[1440px] px-6 pt-28 lg:px-12">
        <h2 className="mb-10 font-serif text-3xl tracking-tight lg:text-4xl">
          The four flagship <span className="italic text-[#D4A574]">IPs</span>.
        </h2>

        <div className="space-y-20">
          {IPS.map((ip, i) => (
            <a
              key={ip.index}
              href={ip.link}
              target="_blank"
              rel="noreferrer"
              data-testid={`ip-${ip.index}`}
              className={`group grid items-center gap-12 lg:grid-cols-2 ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="overflow-hidden border border-[#EDE4D3]/10 card-lift">
                <div className="relative aspect-square w-full overflow-hidden bg-[#11151F]">
                  <PillarImage filename={ip.image} />
                </div>
              </div>

              <div>
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-[#D4A574]">
                  <span>{ip.index}</span>
                  <span className="text-[#EDE4D3]/30">/</span>
                  <span className="text-[#EDE4D3]/55">{ip.tag}</span>
                </div>
                <h3 className="mt-4 font-serif text-3xl leading-tight lg:text-4xl">
                  {ip.title}
                </h3>
                <p className="mt-5 max-w-[55ch] text-[15px] leading-relaxed text-[#EDE4D3]/70">
                  {ip.caption}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 font-serif text-[15px] italic text-[#EDE4D3] transition-colors group-hover:text-[#D4A574]">
                  See the post
                  <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                </span>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Why */}
      <section className="mx-auto mt-32 max-w-[1100px] px-6 lg:px-12">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4A574]">Why IPs &gt; One-off Content</p>
        <ul className="mt-8 grid gap-px overflow-hidden border border-[#EDE4D3]/12 bg-[#EDE4D3]/10 md:grid-cols-2">
          {[
            ["Predictability", "Audiences know when to expect it → higher engagement."],
            ["Efficiency", "Same format, fresh data → high ROI on production."],
            ["Authority", "Month after month of rigour = thought leadership."],
            ["Loyalty", "People come back for this content, not just the brand."],
          ].map(([h, d]) => (
            <li key={h} className="bg-[#0B0E14] p-7">
              <p className="font-serif text-xl text-[#D4A574]">{h}.</p>
              <p className="mt-3 text-sm text-[#EDE4D3]/70">{d}</p>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

function PillarImage({ filename }) {
  // separate inline to inherit ImageFrame placeholder without group hover conflicts
  const ImageFrame = require("@/components/ImageFrame").default;
  return <ImageFrame filename={filename} ratio="1/1" />;
}
